import axiosInstance from '../api/axiosInstance';
import { DomainKey } from '../config/domains';

const baseUrl = process.env.REACT_APP_API_URL;

// How a submission arrived: the onboarding forms or the reseller API
export interface SubmissionSource {
  channel?: 'web' | 'api';
  externalReference?: string | null;
  // only loaded on the detail endpoints; null once the key was deleted
  apiKey?: { id: number; name: string; keyPrefix: string } | null;
}

export interface Precheck extends SubmissionSource {
  id: number;
  genInfo: {
    genComp: string;
    genFullName: string;
    genEmail: string;
  };
  sourceDomain: DomainKey;
  createdAt: string;
  reg: string;
  srv: string[];
}

export interface PrecheckPos extends SubmissionSource {
  id: number;
  company: string;
  contactPerson: string;
  email: string;
  sourceDomain: DomainKey;
  createdAt: string;
  additionalServices: string[];
  taxId: string;
  creditorId: string;
  industry: string;
  address: string;
  locations: string;
  website: string;
  director: string;
  telephone: string;
  mobile: string;
  currentProvider: string;
  turnover: string;
  receiptSize: string;
  bankStatementReference: string;
  terminals: string;
  checkoutConnection: string;
  cashCut: string;
  tip: string;
  cards: string[];
}

type PrecheckType = 'precheck' | 'precheck-pos';

// Admin endpoints with overloads for proper typing
export async function getAll(type: 'precheck'): Promise<Precheck[]>;
export async function getAll(type: 'precheck-pos'): Promise<PrecheckPos[]>;
export async function getAll(type: PrecheckType): Promise<Precheck[] | PrecheckPos[]> {
  const response = await axiosInstance.get(`${baseUrl}/admin/${type}`);
  return response.data;
}

export async function getById(type: 'precheck', id: number): Promise<Precheck>;
export async function getById(type: 'precheck-pos', id: number): Promise<PrecheckPos>;
export async function getById(type: PrecheckType, id: number): Promise<Precheck | PrecheckPos> {
  const response = await axiosInstance.get(`${baseUrl}/admin/${type}/${id}`);
  return response.data;
}

export async function deletePrecheck(type: PrecheckType, id: number): Promise<void> {
  await axiosInstance.delete(`${baseUrl}/admin/${type}/${id}`);
}

export async function getPdf(type: PrecheckType, id: number, language: string = 'en', isAdmin: boolean = true): Promise<Blob> {
  const endpoint = isAdmin ? `/admin/${type}/${id}/pdf` : `/${type}/${id}/pdf`;
  const response = await axiosInstance.get(
    `${baseUrl}${endpoint}?language=${language}`,
    { responseType: 'blob' }
  );
  return new Blob([response.data], { type: 'application/pdf' });
}

// Merchant endpoints
export async function getMerchantPrechecks(): Promise<Precheck[]> {
  const response = await axiosInstance.get(`${baseUrl}/precheck`);
  return response.data;
}

export async function getMerchantPrecheckPoses(): Promise<PrecheckPos[]> {
  const response = await axiosInstance.get(`${baseUrl}/precheck-pos`);
  return response.data;
}

// Form submission endpoints
export async function submitPrecheck(data: Record<string, any>, language: string = 'en', captchaToken?: string): Promise<Precheck> {
  const requestBody = captchaToken ? { ...data, captchaToken } : data;
  const response = await axiosInstance.post(`${baseUrl}/precheck?language=${language}`, requestBody);
  return response.data;
}

export async function submitPrecheckPublic(data: Record<string, any>, language: string = 'en', captchaToken: string): Promise<Precheck> {
  const requestBody = { ...data, captchaToken };
  const response = await axiosInstance.post(`${baseUrl}/public/precheck?language=${language}`, requestBody);
  return response.data;
}

export async function submitPrecheckPos(data: Record<string, any>, language: string = 'en', captchaToken?: string): Promise<PrecheckPos> {
  const requestBody = captchaToken ? { ...data, captchaToken } : data;
  const response = await axiosInstance.post(`${baseUrl}/precheck-pos?language=${language}`, requestBody);
  return response.data;
}

export async function submitPrecheckPosPublic(data: Record<string, any>, language: string = 'en', captchaToken: string): Promise<PrecheckPos> {
  const requestBody = { ...data, captchaToken };
  const response = await axiosInstance.post(`${baseUrl}/public/precheck-pos?language=${language}`, requestBody);
  return response.data;
}

// Blob URLs opened in other tabs. Chrome's built-in PDF viewer re-fetches the
// blob URL when the user hits its download button, so the URL has to stay alive
// for as long as the viewer tab may be open - revoking it early makes that
// download fail with "Check internet connection". They are released when this
// document goes away, which also invalidates them in the browser anyway.
const openedPdfUrls: string[] = [];
let pdfCleanupRegistered = false;

// Helper to open PDF in new tab
export function openPdfInNewTab(blob: Blob): void {
  const url = window.URL.createObjectURL(blob);
  openedPdfUrls.push(url);

  if (!pdfCleanupRegistered) {
    pdfCleanupRegistered = true;
    window.addEventListener('pagehide', () => {
      openedPdfUrls.splice(0).forEach((openedUrl) => window.URL.revokeObjectURL(openedUrl));
    });
  }

  window.open(url, '_blank');
}
